import {
  AlertCircle,
  CheckCircle,
  FileArchive,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useUploadFilesDialog } from "@/features/store-product";
import { useUploadAccountFiles } from "@/features/store-product/model/use-account-files";
import { cn } from "@/shared/lib/css";
import { InfoBlock } from "@/shared/ui/info-block";
import { Button } from "@/shared/ui/kit/button";
import { Image } from "@/shared/ui/kit/image";
import { ResponsiveModal } from "@/shared/ui/responsive-modal";

type UploadStatus = "idle" | "uploading" | "queued" | "error";

export function UploadFilesDialog() {
  const { t } = useTranslation();
  const { isOpen, setIsOpen, data } = useUploadFilesDialog();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("idle");
  const [isDragging, setIsDragging] = useState(false);

  const uploadFiles = useUploadAccountFiles();

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setSelectedFile(null);
        setUploadStatus("idle");
      }, 200);
    }
  }, [isOpen]);

  const handleFileSelect = useCallback(
    (file: File) => {
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (ext !== "zip") {
        toast.error(t("storeProduct.files.onlyZipSupported"));
        return;
      }
      setSelectedFile(file);
      setUploadStatus("idle");
    },
    [t],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const clearFile = useCallback(() => {
    setSelectedFile(null);
    setUploadStatus("idle");
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile || !data) return;

    setUploadStatus("uploading");

    try {
      const result = await uploadFiles.mutateAsync({
        productId: data.productId,
        file: selectedFile,
      });
      setUploadStatus("queued");
      toast.success(
        <div className="flex items-center gap-3">
          <Image
            src={result.product.image_url || undefined}
            alt=""
            className="h-10 w-10 rounded object-contain flex-shrink-0"
          />
          <div>
            <p className="font-medium">{result.product.title}</p>
            <p className="text-sm text-muted-foreground">
              {t("storeProduct.files.uploadedAndProcessing")}
            </p>
          </div>
        </div>,
      );
    } catch (error) {
      setUploadStatus("error");
      const message =
        error instanceof Error
          ? error.message
          : t("storeProduct.files.uploadError");
      toast.error(message);
    }
  }, [selectedFile, data, uploadFiles, t]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const isUploading = uploadStatus === "uploading";

  return (
    <ResponsiveModal open={isOpen} onOpenChange={setIsOpen}>
      <ResponsiveModal.Content className="max-w-md">
        <ResponsiveModal.Header>
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10">
              <Upload className="h-4 w-4 text-primary" />
            </div>
            <div className="flex flex-col gap-1">
              <ResponsiveModal.Title>
                {t("storeProduct.files.title")}
              </ResponsiveModal.Title>
              <ResponsiveModal.Description>
                {data?.productTitle || t("storeProduct.files.uploadingFiles")}
              </ResponsiveModal.Description>
            </div>
          </div>
        </ResponsiveModal.Header>

        <ResponsiveModal.Body>
          <div className="space-y-4">
            {/* Product info */}
            {data && (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                {data.productImageUrl && (
                  <Image
                    src={data.productImageUrl}
                    alt=""
                    className="h-12 w-12 rounded object-contain"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{data.productTitle}</p>
                  <p className="text-xs text-muted-foreground">
                    {data.availableFiles ?? 0}{" "}
                    {t("storeProduct.files.available")}
                  </p>
                </div>
              </div>
            )}

            <InfoBlock
              variant="warning"
              titleKey="storeProduct.info.howToTitle"
              bodyKey={
                data?.productTypeCode === "telegram"
                  ? "storeProduct.info.howToBodyZip"
                  : "storeProduct.info.howToBodyTxt"
              }
            />

            {uploadStatus === "queued" ? (
              <div className="flex flex-col items-center justify-center gap-2 p-4 rounded-md border border-green-500/30 bg-green-500/5">
                <CheckCircle className="h-8 w-8 text-green-500" />
                <div className="text-center">
                  <p className="text-sm font-medium text-green-600">
                    {t("storeProduct.files.uploaded")}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t("storeProduct.files.processingInBackground")}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFile}
                  className="h-8 text-xs mt-2"
                >
                  {t("storeProduct.files.uploadMore")}
                </Button>
              </div>
            ) : (
              <>
                {/* Drop zone */}
                <div
                  className={cn(
                    "relative rounded-md border-2 border-dashed transition-colors",
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "hover:border-primary/50",
                  )}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <label className="flex flex-col items-center justify-center gap-1.5 p-6 cursor-pointer">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                      <Upload className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium">
                        {t("storeProduct.files.dropHere")}
                      </p>
                    </div>
                    <input
                      type="file"
                      accept=".zip"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) =>
                        e.target.files?.[0] &&
                        handleFileSelect(e.target.files[0])
                      }
                      disabled={isUploading}
                    />
                  </label>
                </div>

                {/* Selected file */}
                {selectedFile && (
                  <div
                    className={cn(
                      "flex items-center gap-2 p-3 rounded-md border text-sm",
                      uploadStatus === "error"
                        ? "border-destructive/50 bg-destructive/5"
                        : "bg-muted/30",
                    )}
                  >
                    <FileArchive className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{selectedFile.name}</span>
                    {isUploading && (
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    )}
                    {uploadStatus === "error" && (
                      <AlertCircle className="h-4 w-4 text-destructive" />
                    )}
                    {!isUploading && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={clearFile}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </ResponsiveModal.Body>

        <ResponsiveModal.Footer>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isUploading}
              className="flex-1 h-10"
            >
              {t("common.cancel")}
            </Button>
            {uploadStatus !== "queued" && (
              <Button
                type="button"
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                className="flex-1 h-10"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("storeProduct.files.uploading")}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    {t("storeProduct.files.upload")}
                  </>
                )}
              </Button>
            )}
          </div>
        </ResponsiveModal.Footer>
      </ResponsiveModal.Content>
    </ResponsiveModal>
  );
}
